// frontend/src/hooks/useReportPolling.js
// Shared phased-polling scheduler for "generate a report, wait for it" flows
// (assessment playbooks, roleplay debriefs). Owns no state — callers supply
// a status-fetching adapter and a callback for each resolved tick.

import { useEffect, useRef } from 'react';

export const DEFAULT_POLL_SCHEDULE = {
  initialDelayMs:   5_000,
  phase1DurationMs: 2 * 60 * 1000,
  phase1IntervalMs: 30_000,
  phase2IntervalMs: 15_000,
  totalTimeoutMs:   4 * 60 * 1000,
};

// fetchStatus(signal) must never reject — adapters should catch internally
// and return null on transient errors so the hook just retries on schedule.
// The signal is aborted on unmount/pollKey change; adapters should forward
// it to their underlying request so the in-flight call is actually canceled,
// not just ignored.
export function useReportPolling({ active, pollKey, fetchStatus, onUpdate, schedule = DEFAULT_POLL_SCHEDULE }) {
  const fetchStatusRef = useRef(fetchStatus);
  const onUpdateRef = useRef(onUpdate);
  useEffect(() => { fetchStatusRef.current = fetchStatus; }, [fetchStatus]);
  useEffect(() => { onUpdateRef.current = onUpdate; }, [onUpdate]);

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    let timer;
    const controller = new AbortController();
    const startTime = Date.now();
    const { initialDelayMs, phase1DurationMs, phase1IntervalMs, phase2IntervalMs, totalTimeoutMs } = schedule;

    const tick = async () => {
      const elapsed = Date.now() - startTime;
      if (elapsed >= totalTimeoutMs) {
        if (!cancelled) onUpdateRef.current({ status: 'timed_out' });
        return;
      }

      const report = await fetchStatusRef.current(controller.signal);
      if (cancelled) return;
      if (report) onUpdateRef.current(report);
      if (report?.status === 'completed' || report?.status === 'failed') return;

      const nextElapsed = Date.now() - startTime;
      const interval = nextElapsed < phase1DurationMs ? phase1IntervalMs : phase2IntervalMs;
      timer = setTimeout(tick, interval);
    };

    timer = setTimeout(tick, initialDelayMs);
    return () => { cancelled = true; clearTimeout(timer); controller.abort(); };
  }, [active, pollKey, schedule]);
}
